import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Play, Building2, BookOpen, Award, ChevronUp, ChevronDown, Check, Crown } from 'lucide-react';
import { checkExamPermission, getUserPermissions, UserPermission } from '../utils/permissionCheck';
import { orderService } from '../utils/orderService';
import PackagePurchaseModal from '../components/PackagePurchaseModal';
import GeneratingModal from '../components/GeneratingModal';
import { categoryStore, Organization, CourseCategory, Level } from '../store/categoryStore';
import { getQuestionsDifficulty, getUserWrongQuestionIds } from '../utils/questionStats';
import { supabase } from '../supabase/client';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category_type: string;
  level_text: string;
  duration_minutes: number;
  is_free: boolean;
  created_at: string;
  year?: number;
  month?: number;
  paper_type?: string;
  source?: string;
  total_score?: number;
  remark?: string | null;
  single_choice_count?: number;
  multi_choice_count?: number;
  judge_count?: number;
  coding_count?: number;
}

export default function ExamPlatform() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [activeOrg, setActiveOrg] = useState<string>('');
  const [activeCat, setActiveCat] = useState<string>('');
  const [activeLevel, setActiveLevel] = useState<string>('');

  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);
  const [showPaperList, setShowPaperList] = useState(true);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [singleChoiceCount, setSingleChoiceCount] = useState(0);
  const [judgeCount, setJudgeCount] = useState(0);
  const [programCount, setProgramCount] = useState(0);
  const [difficulty, setDifficulty] = useState('均衡');
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterReal, setFilterReal] = useState(false);
  const [filterMock, setFilterMock] = useState(false);
  const [paperType, setPaperType] = useState<'all' | 'real' | 'mock'>('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const isFilterApplied = activeOrg !== '' && activeCat !== '' && activeLevel !== '';
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [generatingError, setGeneratingError] = useState('');
  const [hasEnoughQuestions, setHasEnoughQuestions] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setCurrentUser(profile || session.user);
        setUserRole(profile?.role || '');
      }
    };
    getSession();

    const loadCategories = async () => {
      await categoryStore.init();
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    });
    fetchPapers();
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (papers.length > 0 && currentUser) checkPermissions();
  }, [papers, currentUser]);

  const fetchPapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('papers')
        .select('*')
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPapers((data as unknown as Paper[]) || []);
    } catch (e) {
      setPapers([]);
    } finally {
      setLoading(false);
    }
  };

  const checkPermissions = async () => {
    if (!currentUser) return;
    const perms = await getUserPermissions(currentUser.id);
    setUserPermissions(perms);
    const permMap: Record<string, boolean> = {};
    for (const paper of papers) {
      const result = await checkExamPermission(
        currentUser.id,
        paper.organization,
        paper.category_type || '',
        paper.level_text || ''
      );
      permMap[paper.id] = result.hasPermission || paper.is_free;
    }
    setPermissions(permMap);
  };

  const uniqueOrgNames = useMemo(() => {
    const orgMap = new Map<string, string>();
    organizations.forEach(o => orgMap.set(o.name, o.id));
    return Array.from(orgMap.entries()).map(([name, id]) => ({ name, id }));
  }, [organizations]);

  const uniqueCatNames = useMemo(() => {
    if (activeOrg === '') return [];
    const cats = categories.filter(c => c.orgId === activeOrg);
    return cats.map(c => ({ name: c.name, id: c.id }));
  }, [categories, activeOrg]);

  const uniqueLevelNames = useMemo(() => {
    if (activeCat === '') return [];
    const lvls = levels.filter(l => l.catId === activeCat);
    return lvls.map(l => ({ name: l.name, id: l.id }));
  }, [levels, activeCat]);

  const currentOrgName = activeOrg === '' ? '' : organizations.find(o => o.id === activeOrg)?.name || '';
  const currentCatName = activeCat === '' ? '' : categories.find(c => c.id === activeCat)?.name || '';
  const currentLevelName = activeLevel === '' ? '' : levels.find(l => l.id === activeLevel)?.name || '';

  const hasCurrentPackagePermission = useMemo(() => {
    if (!currentUser || activeOrg === '' || activeCat === '' || activeLevel === '') return false;
    const orgName = organizations.find(o => o.id === activeOrg)?.name || '';
    const catName = categories.find(c => c.id === activeCat)?.name || '';
    const levelName = levels.find(l => l.id === activeLevel)?.name || '';
    return orderService.checkUserHasPermission(currentUser.id, orgName, catName, levelName);
  }, [currentUser, activeOrg, activeCat, activeLevel, organizations, categories, levels]);

  const resolveCategoryType = (raw: string | null): string | null => {
    if (!raw) return null;
    return categoryStore.getCategoryNameByCode(raw) || raw;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeOrg, activeCat, activeLevel, filterReal, filterMock, searchKeyword, sortOrder]);

  const handleOrgChange = (orgId: string) => {
    setActiveOrg(orgId);
    setActiveCat('');
    setActiveLevel('');
  };

  const handleCatChange = (catId: string) => {
    setActiveCat(catId);
    setActiveLevel('');
  };

  const filteredPapers = papers.filter(paper => {
    const selectedOrgName = activeOrg === '' ? null : organizations.find(o => o.id === activeOrg)?.name;
    const selectedCatName = activeCat === '' ? null : categories.find(c => c.id === activeCat)?.name;
    const selectedLevelName = activeLevel === '' ? null : levels.find(l => l.id === activeLevel)?.name;
    const resolvedType = resolveCategoryType(paper.category_type);
    if (selectedOrgName && paper.organization !== selectedOrgName) return false;
    if (selectedCatName && resolvedType !== selectedCatName && paper.category_type !== selectedCatName) return false;
    if (selectedLevelName && paper.level_text !== selectedLevelName) return false;
    if (filterReal && paper.paper_type !== 'real') return false;
    if (filterMock && paper.paper_type !== 'mock') return false;
    if (searchKeyword.trim()) {
      const displayTitle = paper.title;
      if (!displayTitle.toLowerCase().includes(searchKeyword.trim().toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => {
    const isFreeA = a.is_free ? 1 : 0;
    const isFreeB = b.is_free ? 1 : 0;
    if (isFreeA !== isFreeB) return isFreeB - isFreeA;
    const dateA = (a.year || 0) * 100 + (a.month || 0);
    const dateB = (b.year || 0) * 100 + (b.month || 0);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const totalPapers = filteredPapers.length;
  const totalPages = Math.ceil(totalPapers / pageSize);
  const paginatedPapers = filteredPapers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleStartExam = (paper: Paper) => {
    if (!currentUser) { navigate('/login'); return; }
    const hasPermission = permissions[paper.id] || paper.is_free;
    if (!hasPermission) { setSelectedPaper(paper); setShowPurchaseModal(true); return; }
    navigate(`/exam/${paper.id}`);
  };

  const handleGeneratePaper = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (activeOrg === '' || activeCat === '' || activeLevel === '') {
      alert('请选择级别分类');
      return;
    }
    if (!hasCurrentPackagePermission) {
      const orgData = organizations.find(o => o.id === activeOrg);
      const catData = categories.find(c => c.id === activeCat);
      const levelData = levels.find(l => l.id === activeLevel);
      setSelectedPaper({
        id: 'custom',
        title: `${currentOrgName} ${currentCatName} ${currentLevelName} 自定义试卷`,
        organization: orgData?.name || currentOrgName,
        category_type: catData?.name || currentCatName,
        level_text: levelData?.name || currentLevelName,
        duration_minutes: 60,
        is_free: false,
        created_at: new Date().toISOString()
      } as Paper);
      setShowPurchaseModal(true);
      return;
    }
    setHasEnoughQuestions(true);
    setGeneratingError('');
    setShowGeneratingModal(true);
  };

  const validateQuestions = async (): Promise<{ enough: boolean; error?: string }> => {
    const availableQuestions: any[] = [];
    for (const paper of filteredPapers) {
      const isUnlocked = paper.is_free || permissions[paper.id];
      if (!isUnlocked) continue;
      const { data: questionsData } = await supabase.from('questions').select('*').eq('paper_id', paper.id);
      if (questionsData && questionsData.length > 0) {
        questionsData.forEach((q: any) => {
          availableQuestions.push({
            id: q.id,
            type: q.question_type || 'single_choice',
            title: q.content,
            options: q.options || [],
            score: q.score || 2,
            explanation: q.explanation,
            correctAnswer: q.correct_answer,
            sourcePaperId: paper.id,
            sourcePaperTitle: paper.title
          });
        });
      }
    }

    const questionIds = availableQuestions.map((q: any) => q.id);
    const difficultyMap = await getQuestionsDifficulty(questionIds, 'all');
    const normalizedQuestions = availableQuestions.map((q: any) => ({
      ...q,
      difficulty: (difficultyMap as Record<string, string>)[q.id] || q.difficulty || 'medium'
    }));

    if (difficulty === '错题') {
      const wrongQuestionIds = await getUserWrongQuestionIds(currentUser.id);
      const wrongQuestions = normalizedQuestions.filter((q: any) => wrongQuestionIds.has(q.id));
      let wrongSingleCount = 0, wrongJudgeCount = 0, wrongProgramCount = 0;
      wrongQuestions.forEach((q: any) => {
        const type = q.type;
        if (type === 'single' || type === 'single_choice') wrongSingleCount++;
        else if (type === 'judge') wrongJudgeCount++;
        else if (type === 'code' || type === 'subjective' || type === 'python_coding' || type === 'scratch_coding' || type === 'cpp_coding') wrongProgramCount++;
      });
      const totalWrongNeeded = singleChoiceCount + judgeCount + programCount;
      if (wrongQuestions.length < totalWrongNeeded) return { enough: false, error: '您的错题数量不足，无法纯错题组卷，请减少出题数量或取消仅错题限制' };
      if (singleChoiceCount > 0 && wrongSingleCount < singleChoiceCount) return { enough: false, error: '您的错题数量不足，无法纯错题组卷，请减少出题数量或取消仅错题限制' };
      if (judgeCount > 0 && wrongJudgeCount < judgeCount) return { enough: false, error: '您的错题数量不足，无法纯错题组卷，请减少出题数量或取消仅错题限制' };
      if (programCount > 0 && wrongProgramCount < programCount) return { enough: false, error: '您的错题数量不足，无法纯错题组卷，请减少出题数量或取消仅错题限制' };
      return { enough: true };
    }

    const difficultyFilterMap: Record<string, string[]> = {
      '简单题': ['easy'], '中等题': ['medium'], '难题': ['hard'], '均衡': ['easy', 'medium', 'hard']
    };
    const targetDifficulties = difficultyFilterMap[difficulty] || ['easy', 'medium', 'hard'];
    let filteredQuestions: any[] = [];
    if (difficulty === '简单题' || difficulty === '难题') {
      const targetQuestions = normalizedQuestions.filter((q: any) => targetDifficulties.includes(q.difficulty));
      const mediumQuestions = normalizedQuestions.filter((q: any) => q.difficulty === 'medium');
      filteredQuestions = [...targetQuestions, ...mediumQuestions];
    } else {
      filteredQuestions = normalizedQuestions.filter((q: any) => targetDifficulties.includes(q.difficulty));
    }

    let singleCount = 0, judgeCountStat = 0, programCountStat = 0;
    filteredQuestions.forEach((q: any) => {
      const type = q.type;
      if (type === 'single' || type === 'single_choice') singleCount++;
      else if (type === 'judge') judgeCountStat++;
      else if (type === 'code' || type === 'subjective' || type === 'python_coding' || type === 'scratch_coding' || type === 'cpp_coding') programCountStat++;
    });

    const errors: string[] = [];
    const selectedOrgName = activeOrg === '' ? null : organizations.find(o => o.id === activeOrg)?.name;
    const selectedCatName = activeCat === '' ? null : categories.find(c => c.id === activeCat)?.name;
    const selectedLevelName = activeLevel === '' ? null : levels.find(l => l.id === activeLevel)?.name;
    const filterText = `${selectedOrgName || '全部'}/${selectedCatName || '全部'}/${selectedLevelName || '全部'}/${filterReal ? '真题' : filterMock ? '模拟题' : '全部'}`;
    if (singleChoiceCount > 0 && singleCount < singleChoiceCount) errors.push(`当前筛选条件【${filterText}】下，单选题不足（需要${singleChoiceCount}道，仅有${singleCount}道）`);
    if (judgeCount > 0 && judgeCountStat < judgeCount) errors.push(`当前筛选条件【${filterText}】下，判断题不足（需要${judgeCount}道，仅有${judgeCountStat}道）`);
    if (programCount > 0 && programCountStat < programCount) errors.push(`当前筛选条件【${filterText}】下，编程题不足（需要${programCount}道，仅有${programCountStat}道）`);

    if (errors.length > 0) return { enough: false, error: errors.join('，请调整筛选条件') };
    return { enough: true };
  };

  const handleGeneratingError = (message: string) => {
    setShowGeneratingModal(false);
    setHasEnoughQuestions(true);
    setGeneratingError('');
    alert(message);
  };

  const handleGeneratingComplete = () => {
    setShowGeneratingModal(false);
    const orgData = organizations.find(o => o.id === activeOrg);
    const catData = categories.find(c => c.id === activeCat);
    const levelData = levels.find(l => l.id === activeLevel);
    const userId = currentUser?.id || 'anonymous';
    navigate('/quiz/custom', {
      state: {
        customConfig: {
          org: activeOrg,
          orgName: orgData?.name || '',
          cat: activeCat,
          catName: catData?.name || '',
          level: activeLevel,
          levelName: levelData?.name || '',
          singleChoiceCount,
          judgeCount,
          programCount,
          difficulty,
          paperType,
          userId
        }
      }
    });
  };

  const FilterButton = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button onClick={onClick} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`}>{label}</button>
  );

  const handleExpandFilter = () => {
    setIsFilterExpanded(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 三级筛选栏 */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {isFilterExpanded ? (
              <motion.div
                key="filter-expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 overflow-hidden"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">考评体系</span>
                  <div className="flex gap-2 flex-wrap">
                    {uniqueOrgNames.map(org => <FilterButton key={org.id} label={org.name} active={activeOrg === org.id} onClick={() => handleOrgChange(org.id)} />)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">课程分类</span>
                  <div className="flex gap-2 flex-wrap">
                    {activeOrg !== '' ? (
                      uniqueCatNames.map(cat => <FilterButton key={cat.id} label={cat.name} active={activeCat === cat.id} onClick={() => handleCatChange(cat.id)} />)
                    ) : (
                      <span className="text-sm text-gray-400 px-2 py-1">请先选择考评体系</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">级别分类</span>
                  <div className="flex gap-2 flex-wrap">
                    {activeCat !== '' ? (
                      uniqueLevelNames.map(level => <FilterButton key={level.id} label={level.name} active={activeLevel === level.id} onClick={() => setActiveLevel(level.id)} />)
                    ) : (
                      <span className="text-sm text-gray-400 px-2 py-1">{activeOrg === '' ? '请先选择考评体系' : '请先选择课程分类'}</span>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* 你的选择展示条 - 简洁风格 */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* 左侧：标签和筛选条件 */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-700">你的选择：</span>

              {/* 筛选条件 Pills */}
              <div className="flex items-center gap-2">
                {activeOrg === '' && activeCat === '' && activeLevel === '' ? (
                  <span className="text-sm text-gray-400">请选择筛选条件</span>
                ) : (
                  <>
                    {activeOrg !== '' && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                        {currentOrgName}
                      </span>
                    )}
                    {activeOrg !== '' && activeCat !== '' && (
                      <>
                        <span className="text-gray-400">+</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                          {currentCatName}
                        </span>
                      </>
                    )}
                    {activeOrg !== '' && activeCat !== '' && activeLevel !== '' && (
                      <>
                        <span className="text-gray-400">+</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                          {currentLevelName}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 右侧：展开/收起按钮 */}
            <button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className="flex items-center gap-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 px-4 py-2 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400"
            >
              {isFilterExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  收起
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  展开筛选
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className={`${isFilterApplied ? 'bg-[#F5A623]' : 'bg-[#f5f7fa]'} transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <span className={`font-bold text-2xl whitespace-nowrap ${isFilterApplied ? 'text-white' : 'text-gray-400'}`}>自由组卷</span>
          <div
            className={`flex-1 flex items-center gap-3 ${isFilterApplied ? 'text-white' : 'text-gray-400'}`}
            onClick={(e) => {
              if (!isFilterApplied) {
                e.preventDefault();
                e.stopPropagation();
                alert('请先完成上方的筛选');
              }
            }}
          >
            <span className="whitespace-nowrap font-medium">单选题</span>
            <select value={singleChoiceCount} onChange={(e) => setSingleChoiceCount(Number(e.target.value))} disabled={!isFilterApplied} className={`px-2 py-1.5 rounded text-sm border-0 shadow-sm outline-none ${isFilterApplied ? 'bg-white/95 text-gray-800 focus:ring-2 focus:ring-white/50' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n}>{n}题</option>)}
            </select>
            <span className="whitespace-nowrap font-medium">判断题</span>
            <select value={judgeCount} onChange={(e) => setJudgeCount(Number(e.target.value))} disabled={!isFilterApplied} className={`px-2 py-1.5 rounded text-sm border-0 shadow-sm outline-none ${isFilterApplied ? 'bg-white/95 text-gray-800 focus:ring-2 focus:ring-white/50' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n}>{n}题</option>)}
            </select>
            <span className="whitespace-nowrap font-medium">编程题</span>
            <select value={programCount} onChange={(e) => setProgramCount(Number(e.target.value))} disabled={!isFilterApplied} className={`px-2 py-1.5 rounded text-sm border-0 shadow-sm outline-none ${isFilterApplied ? 'bg-white/95 text-gray-800 focus:ring-2 focus:ring-white/50' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              {Array.from({ length: 51 }, (_, i) => i).map(n => <option key={n} value={n}>{n}题</option>)}
            </select>
            <span className="whitespace-nowrap font-medium">难度等级</span>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} disabled={!isFilterApplied} className={`px-2 py-1.5 rounded text-sm border-0 shadow-sm outline-none ${isFilterApplied ? 'bg-white/95 text-gray-800 focus:ring-2 focus:ring-white/50' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              <option>简单题</option>
              <option>中等题</option>
              <option>难题</option>
              <option>错题</option>
              <option>均衡</option>
            </select>
            <span className="whitespace-nowrap font-medium">试卷类型</span>
            <select value={paperType} onChange={(e) => setPaperType(e.target.value as 'all' | 'real' | 'mock')} disabled={!isFilterApplied} className={`px-2 py-1.5 rounded text-sm border-0 shadow-sm outline-none w-auto ${isFilterApplied ? 'bg-white/95 text-gray-800 focus:ring-2 focus:ring-white/50' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
              <option value="all">全部</option>
              <option value="real">真题</option>
              <option value="mock">模拟题</option>
            </select>
            <button onClick={() => setShowHelpModal(true)} className={`px-3 py-1.5 border rounded text-sm transition-colors ml-2 ${isFilterApplied ? 'border-white/60 text-white hover:bg-white/20' : 'border-gray-300 text-gray-400 hover:bg-gray-100'}`}>组卷说明</button>
          </div>
          <button onClick={() => {
            if (!isFilterApplied) {
              alert('请先完成上方的筛选');
              return;
            }
            handleGeneratePaper();
          }} className={`px-6 py-2 font-bold rounded-lg transition-colors whitespace-nowrap ${isFilterApplied ? 'bg-white text-orange-600 hover:bg-gray-100' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>生成试卷</button>
        </div>
      </div>

      <div className="bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="py-3 px-4 border-b border-[#e5e7eb]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <span className="text-xl font-bold text-gray-900">试卷列表</span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${filterReal ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                      {filterReal && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <input type="checkbox" checked={filterReal} onChange={(e) => setFilterReal(e.target.checked)} className="hidden" />
                    <span className={`text-sm font-medium transition-colors ${filterReal ? 'text-blue-600' : 'text-gray-600 group-hover:text-blue-600'}`}>真题</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${filterMock ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                      {filterMock && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <input type="checkbox" checked={filterMock} onChange={(e) => setFilterMock(e.target.checked)} className="hidden" />
                    <span className={`text-sm font-medium transition-colors ${filterMock ? 'text-blue-600' : 'text-gray-600 group-hover:text-blue-600'}`}>模拟题</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input type="text" value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)} placeholder="搜索试卷名称" className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48 transition-all" />
                  </div>
                </div>
              </div>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')} className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-50 transition-colors cursor-pointer">
                <option value="desc">按时间降序</option>
                <option value="asc">按时间升序</option>
              </select>
            </div>
          </div>

          <div className="py-4">
            <AnimatePresence>
              {showPaperList && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-2 overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                  ) : paginatedPapers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">当前分类下暂无试卷，请尝试调整筛选条件</div>
                  ) : (
                    paginatedPapers.map(paper => {
                      const hasPermission = permissions[paper.id] || paper.is_free;
                      const statusText = paper.is_free ? '免费' : hasPermission ? '已解锁' : '付费';
                      const statusClass = paper.is_free ? 'text-green-600' : hasPermission ? 'text-blue-600' : 'text-amber-600';
                      const displayTitle = paper.title;
                      const displayDuration = paper.duration_minutes || 60;
                      const displayTotalScore = paper.total_score || 0;
                      return (
                        <motion.div key={paper.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded p-3 flex items-center justify-between hover:bg-[#f3f4f6] transition-colors">
                          <div className="flex-1 min-w-0 flex items-center gap-4">
                            <h3 className="font-medium text-gray-900 truncate">{displayTitle}</h3>
                            {paper.remark && <span className="text-sm text-blue-600 whitespace-nowrap">{paper.remark}</span>}
                          </div>
                          <div className="flex items-center mx-6 text-sm text-gray-600 flex-shrink-0">
                            <div className="w-16 text-left"><span className="font-medium text-blue-600">{displayTotalScore}</span>分</div>
                            <div className="w-20 text-left"><span className="font-medium">{(paper.single_choice_count || 0) + (paper.multi_choice_count || 0) + (paper.judge_count || 0) + (paper.coding_count || 0)}</span>道题</div>
                            <div className="w-24 text-left flex items-center gap-1"><Clock className="w-4 h-4" />{displayDuration}分钟</div>
                            <div className="w-16 text-left"><span className={`font-medium ${statusClass}`}>{statusText}</span></div>
                          </div>
                          {paper.is_free || permissions[paper.id] ? (
                            <button onClick={() => handleStartExam(paper)} className="px-5 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 flex-shrink-0">
                              <Play className="w-4 h-4" />开始答题
                            </button>
                          ) : (
                            <button onClick={() => handleStartExam(paper)} className="px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded font-medium hover:from-amber-600 hover:to-orange-600 transition-colors flex items-center gap-2 flex-shrink-0 shadow-sm">
                              <Crown className="w-4 h-4" />付费解锁
                            </button>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-4 border-t border-gray-200">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200">上一页</button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{pageNum}</button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200">下一页</button>
                <span className="text-sm text-gray-500 ml-2">共 {totalPages} 页，{totalPapers} 条</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPurchaseModal && selectedPaper && (
        <PackagePurchaseModal paper={selectedPaper} onClose={() => setShowPurchaseModal(false)} role={userRole === 'teacher' ? 'teacher' : 'student'} />
      )}

      <GeneratingModal
        isOpen={showGeneratingModal}
        onComplete={handleGeneratingComplete}
        onError={handleGeneratingError}
        onClose={() => setShowGeneratingModal(false)}
        onValidate={validateQuestions}
        singleChoiceCount={singleChoiceCount}
        judgeCount={judgeCount}
        programCount={programCount}
      />

      {showHelpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">组卷说明</h2>
              <button onClick={() => setShowHelpModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">1. 题型与题量设置</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>单选题、判断题、编程题均可独立设置题量，题量上限以当前分类下的题库题目总数为准。</li>
                  <li>若设置题量超过可用题目总数，系统将自动按最大值抽取，不会生成空白题目。</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">2. 难度等级说明</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li><span className="font-medium">简单题：</span>优先抽取难度较低的基础题目，适合入门练习。</li>
                  <li><span className="font-medium">中等题：</span>优先抽取难度适中的常规题目，适合巩固练习。</li>
                  <li><span className="font-medium">难题：</span>优先抽取难度较高的挑战题目，适合拔高训练。</li>
                  <li><span className="font-medium">错题：</span>仅从你做过的错题中抽取，适合针对性查漏补缺。</li>
                  <li><span className="font-medium">均衡：</span>按简单、中等、难题均匀分布抽取，模拟真实考试难度。</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">3. 生成与使用</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>点击「生成试卷」后，系统会根据你设置的条件随机抽取题目，每次生成的题目组合可能不同。</li>
                  <li>生成的试卷可直接在线作答，提交后可即时查看成绩、答案与解析。</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">4. 温馨提示</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>错题模式仅在你有错题记录时生效，若暂无错题，将自动切换为均衡模式。</li>
                  <li>如遇题目不足，可尝试调整筛选条件或题量设置。</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
